import { useState, useEffect, Fragment } from 'react';
import { locationShape, routerShape } from 'react-router';
import { replace } from 'react-router-redux';
import { connect } from 'react-redux';
import lodash from 'lodash';
import { SubFormRouter, SubFormRoute } from 'forms/containers/SubFormRouter/SubFormRouter';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { Container, FormSection } from 'forms/components/common';
import { Recipient } from 'forms/components/DelegationOfAuthority/Recipient';
import { SigningIframe } from 'part11/components/SigningIframe/SigningIframe';
import { signDOATask, getDOATask, unloadDOATask, cancelDOATask } from 'forms/actions/doa';

import LoadingComponent from 'common/permissions/components/LoadingComponent';
import DOADetails from 'forms/components/DelegationOfAuthority/DOADetails';

import { getAuthCredentials } from 'common/util/auth-storage';
import { openPopup, listenForCredentials } from 'common/util/popup';
import { fetchSignUrl } from 'common/actions/login';

export function DOATask(props) {
    const [taskActionSent, setTaskActionSent] = useState(false);

    useEffect(() => {
        props.getDOATask(props.signatureRequestId);

        return () => {
            props.unloadDOATask();
        };
    }, []);

    useEffect(() => {
        if (props.submissionSuccess) {
            const { replace, location } = props;
            const newLocation = lodash.cloneDeep(location);
            newLocation.pathname = '/inbox';
            replace(newLocation);
        }
    }, [props.submissionSuccess]);

    const getSigningUrl = () => {
        const accessToken = getAuthCredentials().accessToken;
        return `${props.originUrl}/sign?access_token=${accessToken}&action_text=Complete Signature Request`;
    };

    const onSignDOASignatureRequest = (signingToken) => {
        setTaskActionSent(true);
        props.signDOATask({ signingToken });
    };

    const { signatureRequest, taskBox, userEmailAddress } = props;
    const signatureRequestEmailAddress = lodash.get(props, 'signatureRequest.requestedFrom.emailAddress', '');
    const permissionToSignTask = userEmailAddress === signatureRequestEmailAddress;
    const signingUrl = getSigningUrl();
    const isLoading = lodash.isNil(signatureRequest);
    const isSending = taskActionSent;

    return (
        <Fragment data-cy="DOATask">
            <LoadingSpinner loading={isLoading} sending={isSending} />

            {!isLoading && !isSending && (
                <SubFormRouter>
                    <SubFormRoute>
                        <Container>
                            <DOADetails taskBox={taskBox} signatureRequest={signatureRequest} />

                            <TaskSigningIframe
                                signingUrl={signingUrl}
                                permissionToSignTask={permissionToSignTask}
                                onSignDOASignatureRequest={onSignDOASignatureRequest.bind(this)}
                                taskBox={taskBox}
                                addApplicationMessage={props.addApplicationMessage}
                                isSSOSigningEnabled={Boolean(lodash.get(props.featureAccess, 'ssoESign'))}
                                isSsoUser={lodash.get(props.user, 'isSso', false)}
                                originUrl={props.originUrl}
                            />

                            <RecipientSection
                                signatureRequest={signatureRequest}
                                taskBox={taskBox}
                                cancelDOATask={props.cancelDOATask}
                            />
                        </Container>
                    </SubFormRoute>
                </SubFormRouter>
            )}
        </Fragment>
    );
}

const LoadingSpinner = ({ loading, sending }) => {
    if (!loading && !sending) {
        return null;
    }

    let action = 'Loading';
    if (sending) {
        action = 'Completing';
    }

    const text = `${action} task ...`;
    return <LoadingComponent text={text} />;
};

const RecipientSection = ({ signatureRequest, taskBox, cancelDOATask }) => {
    if (taskBox === 'sent') {
        return (
            <Recipient
                taskId={signatureRequest.id}
                displayName={signatureRequest.requestedFrom.displayName}
                emailAddress={signatureRequest.requestedFrom.emailAddress}
                status={signatureRequest.status}
                cancelTask={() => cancelDOATask()}
                data-cy="DOATask-Recipient"
            />
        );
    }

    return null;
};

const TaskSigningIframe = ({
    signingUrl,
    onSignDOASignatureRequest,
    permissionToSignTask,
    taskBox,
    addApplicationMessage,
    isSSOSigningEnabled,
    isSsoUser,
    originUrl,
}) => {
    if (taskBox === 'sent') {
        return null;
    }
    if (permissionToSignTask) {
        if (isSSOSigningEnabled && isSsoUser) {
            const accessToken = getAuthCredentials().accessToken;
            return (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem', marginBottom: '2rem' }}>
                    <button
                        style={{
                            backgroundColor: '#2b5ea7', // color--blue1 equivalent or secondary-color
                            color: 'white',
                            border: 'none',
                            padding: '0.75rem 1.5rem',
                            fontSize: '1rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}
                        onClick={() => {
                            fetchSignUrl(accessToken, true, originUrl).then(res => {
                                const popup = openPopup(res.provider_uri, 'sign');
                                listenForCredentials(popup)
                                    .then(res => onSignDOASignatureRequest(res.token))
                                    .catch(err => addApplicationMessage('error', err.message || 'SSO authentication failed.'));
                            });
                        }}>
                        Authenticate with SSO to Sign
                    </button>
                </div>
            );
        }
        return (
            <FormSection data-cy="DOATask-TaskSigningIframe">
                <SigningIframe url={signingUrl} signDocument={onSignDOASignatureRequest} />
            </FormSection>
        );
    } else {
        addApplicationMessage('error', 'You are not permitted to sign this task.');
        return null;
    }
};

DOATask.contextTypes = {
    router: routerShape.isRequired,
    location: locationShape.isRequired,
};

export default connect(
    (state, props) => {
        const userEmailAddress = lodash.get(state, 'me.email', '');
        const signatureRequest = lodash.get(state, 'form.signatureRequest', null);
        const signatureRequestId = lodash.get(props, 'params.id');
        const taskBox = lodash.get(props, 'location.query.taskBox', 'inbox');
        const originUrl = lodash.get(state, 'application.config.corsOriginUrl', null);
        const submissionSuccess = lodash.get(state, 'form.submissionSuccess', null);

        return {
            signatureRequest,
            originUrl,
            submissionSuccess,
            signatureRequestId,
            taskBox,
            userEmailAddress,
            user: state.me,
            featureAccess: lodash.get(state, `me.featureAccess.${lodash.get(state, 'me.institution.id')}`, {}),
        };
    },
    {
        replace,
        signDOATask,
        getDOATask,
        unloadDOATask,
        cancelDOATask,
        addApplicationMessage,
    }
)(DOATask);
